/**
 * Drag-resize Meeting Tools sidebar (left edge).
 * Width clamped between MIN_WIDTH and 40% of the viewport.
 *
 * Lift this hook to LiveSessionRoom so rail + open-tool panel share width.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

export const MEETING_TOOLS_DEFAULT_WIDTH = 208;
const MIN_WIDTH = 180;
const MAX_RATIO = 0.55;

export function clampMeetingToolsWidth(px: number): number {
  if (typeof window === "undefined") return px;
  const max = Math.floor(window.innerWidth * MAX_RATIO);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(px)));
}

export function useMeetingToolsResize(
  defaultWidth: number = MEETING_TOOLS_DEFAULT_WIDTH,
) {
  const [width, setWidth] = useState(() =>
    clampMeetingToolsWidth(defaultWidth),
  );
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Left edge: drag left → wider, drag right → narrower
      const next = drag.startWidth + (drag.startX - e.clientX);
      setWidth(clampMeetingToolsWidth(next));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setWidth((w) => clampMeetingToolsWidth(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = { startX: e.clientX, startWidth: width };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width],
  );

  return { width, setWidth, onResizePointerDown };
}

export function MeetingToolsResizeHandle({
  onPointerDown,
}: {
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize Meeting Tools"
      title="Drag to resize"
      onPointerDown={onPointerDown}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 8,
        marginLeft: -4,
        cursor: "col-resize",
        zIndex: 5,
        touchAction: "none",
      }}
    />
  );
}
